import PropTypes from "prop-types";
import merge from "lodash/merge";
import { useEffect, useMemo } from "react";
// @mui
import CssBaseline from "@mui/material/CssBaseline";
import { createTheme, ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers";

// moment
import moment from "moment";
import "moment/locale/es";
import "moment/locale/pt";

// locales
import { useLocales } from "src/locales";

// auth
import { useAuthContext } from "src/auth/hooks";

// components
import { useSettingsContext } from "src/components/settings";
// DevExtreme
import themes from "devextreme/ui/themes";
import { locale } from "devextreme/localization";
// system
import { palette } from "./palette";
import { shadows } from "./shadows";
import { typography } from "./typography";
import { customShadows } from "./custom-shadows";
import { componentsOverrides } from "./overrides";
// options
import { presets } from "./options/presets";
import { darkMode } from "./options/dark-mode";
import { contrast } from "./options/contrast";
import RTL, { direction } from "./options/right-to-left";

// ----------------------------------------------------------------------

export default function ThemeProvider({ children }) {
  const { currentLang } = useLocales();
  const { user } = useAuthContext();

  const settings = useSettingsContext();

  const darkModeOption = darkMode(settings.themeMode);

  const presetsOption = presets(settings.themeColorPresets);

  const contrastOption = contrast(settings.themeContrast === "bold", settings.themeMode);

  const directionOption = direction(settings.themeDirection);

  const baseOption = useMemo(
    () => ({
      palette: palette("light"),
      shadows: shadows("light"),
      customShadows: customShadows("light"),
      typography,
      shape: { borderRadius: 8 },
    }),
    []
  );

  const memoizedValue = useMemo(
    () =>
      merge(
        // Base
        baseOption,
        // Direction: remove if not in use
        directionOption,
        // Dark mode: remove if not in use
        darkModeOption,
        // Presets: remove if not in use
        presetsOption,
        // Contrast: remove if not in use
        contrastOption.theme
      ),
    [baseOption, directionOption, darkModeOption, presetsOption, contrastOption.theme]
  );

  const theme = createTheme(memoizedValue);

  theme.components = merge(componentsOverrides(theme), contrastOption.components, {
    MuiButton: {
      defaultProps: {
        variant: "soft",
      },
      variants: [
        {
          props: { variant: "soft" },
        },
      ],
    },
  });

  const themeWithLocale = useMemo(
    () => createTheme(theme, currentLang.systemValue),
    [currentLang.systemValue, theme]
  );

  useEffect(() => {
    themes.current(`material.${settings.themeColorPresets}.${settings.themeMode}`);
  }, [settings.themeColorPresets, settings.themeMode]);

  useEffect(() => {
    // isoValue: ES -> , | US -> .
    const isoValue = user?.numberFormat === "," ? "ES" : "US";
    locale(`${currentLang.value}-${isoValue}`);
    moment.locale(currentLang.value);
  }, [currentLang, user]);

  return (
    <MuiThemeProvider theme={themeWithLocale}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <RTL themeDirection={settings.themeDirection}>
          <CssBaseline />
          {children}
        </RTL>
      </LocalizationProvider>
    </MuiThemeProvider>
  );
}

ThemeProvider.propTypes = {
  children: PropTypes.node,
};
